import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Evidence } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export interface CaptureFields {
  latitude?: number | null;
  longitude?: number | null;
  capturedAt?: string;
  note?: string;
}

export async function listEvidence(fileId: string): Promise<Evidence[]> {
  const { data } = await api.get<Evidence[]>(`/files/${fileId}/evidence`);
  return data;
}

export async function uploadEvidence(
  fileId: string,
  photo: File,
  fields: CaptureFields,
): Promise<Evidence> {
  // Let the browser set the multipart boundary — don't set Content-Type by hand.
  const form = new FormData();
  form.append('photo', photo);
  if (fields.latitude != null) form.append('latitude', String(fields.latitude));
  if (fields.longitude != null) form.append('longitude', String(fields.longitude));
  if (fields.capturedAt) form.append('capturedAt', fields.capturedAt);
  if (fields.note?.trim()) form.append('note', fields.note.trim());
  const { data } = await api.post<Evidence>(`/files/${fileId}/evidence`, form);
  return data;
}

export const evidenceKeys = {
  forFile: (fileId: string) => ['evidence', fileId] as const,
};

export function useEvidence(fileId: string | undefined) {
  return useQuery({
    queryKey: evidenceKeys.forFile(fileId ?? ''),
    queryFn: () => listEvidence(fileId as string),
    enabled: !!fileId,
  });
}

export function useUploadEvidence(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({ photo, fields }: { photo: File; fields: CaptureFields }) =>
      uploadEvidence(fileId as string, photo, fields),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: evidenceKeys.forFile(fileId) });
      toast.success('gov.capture.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.capture.saveError'),
  });
}
